import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useEstadoDeLaCaja, type Tono } from "@/context/caja/estadoDeLaCaja";
import { useFotoDeLaCaja } from "@/context/caja/fotoDeLaCaja";
import { indicadorDelEquipo } from "@/context/caja/panelDeLaCaja";
import { useTerminalDelEquipo } from "@/hooks/react-query/Caja/useTerminalDelEquipo";
import CargarMiPin from "@/pages/orders/crear/quick/caja/CargarMiPin";
import { PanelDeLaCaja } from "./PanelDeLaCaja";

const PUNTO: Record<Tono, string> = {
  ok: "bg-success",
  aviso: "bg-warning",
  error: "bg-error",
  neutro: "bg-text-secondary",
};

/** Tarda más que un parpadeo: el chip de «abriendo» no aparece de pasada. */
const ABRIENDO_MS = 1500;

function useTardando(activo: boolean, ms: number): boolean {
  const [tarde, setTarde] = useState(false);
  useEffect(() => {
    if (!activo) {
      setTarde(false);
      return;
    }
    const espera = setTimeout(() => setTarde(true), ms);
    return () => clearTimeout(espera);
  }, [activo, ms]);
  return activo && tarde;
}

/**
 * La caja en la barra (#344 AC-2): conectado, con problemas o sin conexión, y
 * cuántas ventas faltan subir. Si el almacén no abrió o todavía está abriendo,
 * también lo dice. Abre el panel con todo el estado de la caja.
 */
function IndicadorDeLaCaja() {
  const estado = useEstadoDeLaCaja();
  const almacen = useFotoDeLaCaja((s) => s.almacen);
  const { cargando, terminal } = useTerminalDelEquipo();
  const abriendo = useTardando(cargando, ABRIENDO_MS);
  const [abierto, setAbierto] = useState(false);
  // El diálogo del PIN vive acá: si viviera en el panel, se iría con él.
  const [cargandoPin, setCargandoPin] = useState(false);
  const indicador = indicadorDelEquipo(estado, almacen, abriendo);
  if (!indicador) return null;

  const pendientes =
    estado.pendientes > 0 && indicador.etiqueta !== "Caja no disponible"
      ? ` · ${estado.pendientes} ${
          estado.pendientes === 1 ? "venta sin subir" : "ventas sin subir"
        }`
      : "";

  return (
    <>
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="status"
            aria-label={`Caja: ${indicador.etiqueta}${pendientes}`}
            title={indicador.detalle}
            // El chip mide 26 px de alto: el ::after extiende el clic a 40 px.
            className="relative flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-medium text-text-primary whitespace-nowrap tabular-nums hover:bg-background-200 transition-colors after:absolute after:inset-x-0 after:-inset-y-2"
          >
            <span
              aria-hidden
              className={cn("h-2 w-2 rounded-full", PUNTO[indicador.tono])}
            />
            {indicador.etiqueta}
            {pendientes}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96">
          <PanelDeLaCaja
            onIr={() => setAbierto(false)}
            onCargarPin={() => {
              setAbierto(false);
              setCargandoPin(true);
            }}
          />
        </PopoverContent>
      </Popover>
      {cargandoPin && terminal && (
        <CargarMiPin
          terminal={terminal}
          onClose={() => setCargandoPin(false)}
        />
      )}
    </>
  );
}

export default IndicadorDeLaCaja;
